import { BrandMark } from "@/components/brand";
import { useBranchingCanvas } from "@/hooks/useBranchingCanvas";
import { type Conversation, useConversationHistory } from "@/hooks/useConversationHistory";
import { useInteractiveWidget } from "@/hooks/useInteractiveWidget";
import { createLogger } from "@/lib/logger";
import { supabase } from "@/lib/supabaseClient";
import { cn } from "@/lib/utils";
import { User } from "lucide-react";
import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { TranscriptSegment } from "../models/podcast";
import { ChatHeader, ChatMessage, MessageInput } from "./practice-chat";
import {
  WHITEBOARD_INLINE_FRAME_CLASS,
  WhiteboardPhotoFrame,
} from "./whiteboard/WhiteboardPhotoFrame";

const logger = createLogger("PracticeChat");

function WidgetPanelSkeleton() {
  return (
    <WhiteboardPhotoFrame
      className={`${WHITEBOARD_INLINE_FRAME_CLASS} my-5`}
      role="status"
      aria-label="Loading exercise whiteboard"
    >
      <div className="whiteboard-card--photo whiteboard-writing-surface relative w-full space-y-5 pr-12 pt-2 text-[#162033] sm:pr-14">
        <p className="text-lg font-semibold">Preparing your exercise...</p>
        <div className="h-3 w-5/6 rounded-full bg-slate-700/15" />
        <div className="h-3 w-2/3 rounded-full bg-slate-700/15" />
      </div>
    </WhiteboardPhotoFrame>
  );
}

const InteractiveWidget = dynamic(
  () => import("./InteractiveWidget").then((module) => module.InteractiveWidget),
  {
    loading: () => <WidgetPanelSkeleton />,
    ssr: false,
  }
);

const BranchingCanvas = dynamic(
  () => import("./widgets/BranchingCanvas").then((module) => module.BranchingCanvas),
  {
    loading: () => <WidgetPanelSkeleton />,
    ssr: false,
  }
);

const FlowCanvasWidget = dynamic(
  () => import("./widgets/FlowCanvasWidget").then((module) => module.FlowCanvasWidget),
  {
    loading: () => <WidgetPanelSkeleton />,
    ssr: false,
  }
);

interface PracticeChatProps {
  segmentToExplain?: TranscriptSegment;
  languageName: string;
  fromLanguageName?: string;
  languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  showLearningCanvas?: boolean;
  onClose?: () => void;
  onToggleSidebar?: () => void;
  userId?: string;
  currentConversation?: Conversation | null;
  onMessagesUpdate?: (messages: Array<{ role: string; content: string }>) => void;
  onUserMessageSubmitted?: () => void | Promise<void>;
  forceNewConversation?: boolean; // Add prop to indicate this is a forced new conversation
  onContinueSession?: () => void;
}

const PracticeChat: React.FC<PracticeChatProps> = ({
  segmentToExplain,
  languageName,
  fromLanguageName,
  languageLevel,
  showLearningCanvas = true,
  onClose,
  onToggleSidebar,
  userId,
  currentConversation,
  onMessagesUpdate,
  onUserMessageSubmitted,
  forceNewConversation = false,
  onContinueSession,
}) => {
  const [practiceMessages, setPracticeMessages] = useState<
    Array<{ role: string; content: string }>
  >(() => {
    if (currentConversation?.messages) {
      return currentConversation.messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
    }
    return [];
  });
  const [isPracticeThinking, setIsPracticeThinking] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Widget state from hook
  const {
    showWidget,
    isWidgetLoading,
    widgetError,
    openWidget,
    closeWidget,
    // A2UI Flow Canvas
    flowCanvasMessages,
    isFlowCanvasLoading,
    flowCanvasError,
    showFlowCanvas,
    generateFlowCanvas,
    closeFlowCanvas,
  } = useInteractiveWidget();

  // The prompt sent to the exercise generator when the widget opens
  const [widgetPrompt, setWidgetPrompt] = useState("");

  // Branching Canvas - Concept Tree visualization
  const branchingCanvas = useBranchingCanvas({
    messages: practiceMessages.map((m) => ({
      role: m.role as "user" | "assistant",
      content: m.content,
      timestamp: Date.now(),
    })),
    targetLanguage: languageName,
    languageLevel,
    enabled: showLearningCanvas,
  });

  // Load messages from current conversation
  // Sync messages if currentConversation updates while mounted
  useEffect(() => {
    if (currentConversation) {
      setPracticeMessages((prev) => {
        const nextMessages = currentConversation.messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

        // Simple optimization to avoid unnecessary re-renders if content is identical
        if (
          prev.length === nextMessages.length &&
          prev.every(
            (m, i) => m.role === nextMessages[i].role && m.content === nextMessages[i].content
          )
        ) {
          return prev;
        }

        return nextMessages;
      });
    } else {
      setPracticeMessages([]);
    }
  }, [currentConversation]);

  useLayoutEffect(() => {
    if (!currentConversation?.id) return;

    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [currentConversation?.id]);

  // Update conversation when messages change
  // Use a stable ref for the callback to avoid re-triggering this effect
  // when the parent recreates the callback (e.g., due to state changes),
  // which could otherwise cause a refresh loop.
  const onMessagesUpdateRef = useRef(onMessagesUpdate);
  useEffect(() => {
    onMessagesUpdateRef.current = onMessagesUpdate;
  }, [onMessagesUpdate]);

  useEffect(() => {
    if (practiceMessages.length > 0 && onMessagesUpdateRef.current) {
      onMessagesUpdateRef.current(practiceMessages);
    }
  }, [practiceMessages]);

  // Handle copying message content
  const handleCopyMessage = useCallback(async (content: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageIndex(null);
      }, 2000);
    } catch (error) {
      logger.error("Failed to copy message", { error });
    }
  }, []);

  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onClose) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  // Handle sending a message in practice mode
  const handlePracticeMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) return;

      // Add user message to history
      setPracticeMessages((prev) => [...prev, { role: "user", content: message }]);
      setIsPracticeThinking(true);

      try {
        // Only explicit learner submissions count, never greetings/history hydration.
        void Promise.resolve()
          .then(() => onUserMessageSubmitted?.())
          .catch(() => logger.warn("Failed to record written practice activity"));
        // Prepare messages for the API call including conversation history.
        // The system prompt is now handled by the backend.
        const messages = [
          ...practiceMessages.slice(-6), // keep context
          { role: "user", content: message },
        ];

        const response = await fetch("/api/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages,
            userId: userId || "anonymous",
            agentId: `language-tutor-${userId || "anonymous"}`,
            languageName: currentConversation?.languageName || languageName,
            languageLevel: currentConversation?.languageLevel || languageLevel,
            fromLanguageName,
          }),
        });

        if (!response.ok) {
          let errorDetailsMessage = `Failed to fetch response. Status: ${response.status}`;
          try {
            const errorData = (await response.json()) as any;
            if (errorData && errorData.error) {
              errorDetailsMessage = `API Error: ${errorData.error}`;
            }
          } catch (e) {
            // If parsing error response fails, stick to the status text
            errorDetailsMessage = `Failed to fetch response. Status: ${response.status}. Unable to parse error body.`;
          }
          throw new Error(errorDetailsMessage);
        }

        const data = (await response.json()) as any;
        // The backend now returns the full message object from the Responses API
        // The actual text is in response.content[0].text
        const aiResponse = data.response.content[0].text;

        setPracticeMessages((prev) => [...prev, { role: "assistant", content: aiResponse }]);
      } catch (error: any) {
        // Add :any to error to access properties like message/stack
        logger.error("Error sending message", {
          details: error,
          message: error?.message,
          stack: error?.stack,
        });
        // Fallback response
        const fallbackResponse =
          "I'm having trouble connecting right now. Please try again in a moment.";
        setPracticeMessages((prev) => [...prev, { role: "assistant", content: fallbackResponse }]);
      } finally {
        setIsPracticeThinking(false);

        // Scroll to bottom after messages update
        setTimeout(() => {
          const chatContainer = document.getElementById("practice-chat-messages");
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
      }
    },
    [
      practiceMessages,
      languageName,
      languageLevel,
      userId,
      fromLanguageName,
      currentConversation,
      onUserMessageSubmitted,
    ]
  ); // Add practiceMessages, languageName, and userId as dependencies

  // Handle creating a session summary
  const handleCreateSummary = useCallback(async () => {
    try {
      setIsSummarizing(true);
      // Attach auth if available for RLS inserts
      const { data } = await supabase.auth.getSession();
      const access = data?.session?.access_token;
      const authHeaders: Record<string, string> = access
        ? { Authorization: `Bearer ${access}` }
        : {};

      const payload = {
        messages: practiceMessages.slice(-50),
        conversationId: currentConversation?.id,
        languageName,
        languageLevel,
        fromLanguageName,
        sessionMode: "conversation",
      };

      const [res, diaryRes] = await Promise.all([
        fetch("/api/progress/session-summary", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        }),
        fetch("/api/progress/tutor-diary", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to create summary (${res.status})`);
      }

      const json = (await res.json()) as any;
      const summary: string = json?.summary || "";
      if (!diaryRes.ok) {
        logger.warn("Tutor diary save failed (non-fatal)", { status: diaryRes.status });
      }
      if (summary) {
        setPracticeMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Session Summary:\n${summary}` },
        ]);
        // Scroll to bottom after appending summary
        setTimeout(() => {
          const chatContainer = document.getElementById("practice-chat-messages");
          if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
        }, 50);
      }
    } catch (err: any) {
      logger.error("Failed to create session summary", {
        message: err?.message,
        stack: err?.stack,
      });
    } finally {
      setIsSummarizing(false);
    }
  }, [
    practiceMessages,
    currentConversation?.id,
    languageName,
    languageLevel,
    fromLanguageName,
    currentConversation?.languageName,
    currentConversation?.languageLevel,
  ]);

  // Handle generating an interactive whiteboard exercise
  const handleGenerateWidget = useCallback(() => {
    const recentContext = practiceMessages
      .slice(-5)
      .map((m) => `${m.role}: ${m.content}`)
      .join("\n");

    const effectiveLanguageName = currentConversation?.languageName || languageName;
    const effectiveLanguageLevel = currentConversation?.languageLevel || languageLevel;

    const prompt = `Create a SINGLE interactive exercise (one component only) for a ${effectiveLanguageLevel} ${effectiveLanguageName} learner based on this conversation:\n${recentContext}\n\nChoose the single best component type for this context. Do not output multiple components.`;
    setWidgetPrompt(prompt);
    openWidget();
    setTimeout(() => {
      const chatContainer = document.getElementById("practice-chat-messages");
      if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
    }, 10);
  }, [openWidget, practiceMessages, languageName, languageLevel, currentConversation]);

  // Handle generating Flow Canvas hint
  const handleGiveHint = useCallback(() => {
    generateFlowCanvas(practiceMessages, languageName, languageLevel);
  }, [generateFlowCanvas, practiceMessages, languageName, languageLevel]);

  // TTS speak function for Flow Canvas hints
  const handleSpeakText = useCallback(
    async (text: string) => {
      if ("speechSynthesis" in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        // Try to find a voice for the target language (e.g. "es-ES", "fr-FR")
        // Simple heuristic: match standard BCP 47 tags or just language code
        const voices = speechSynthesis.getVoices();
        // Heuristic: map full language name to a likely locale code if possible, or just guess
        // For now, let's rely on browser defaults or simple matching if languageName has a code
        // We don't have the code readily available in props (only name), so this is best effort.
        // Ideally we'd map "Spanish" -> "es", "French" -> "fr" etc.
        // But props has `languageName` string.
        // Let's just try to match the language name in the voice name as fallback.
        const matchingVoice = voices.find(
          (v) =>
            v.lang.toLowerCase().includes(languageName.toLowerCase()) ||
            v.name.toLowerCase().includes(languageName.toLowerCase())
        );
        if (matchingVoice) {
          utterance.voice = matchingVoice;
        }
        speechSynthesis.speak(utterance);
      }
    },
    [languageName]
  );

  // Handle selecting a path from Flow Canvas
  const handleSelectPath = useCallback(
    (text: string) => {
      handlePracticeMessage(text);
    },
    [handlePracticeMessage]
  );

  // Track which conversation we've initialized to prevent double initialization
  const initializedConversationRef = useRef<string | null>(null);

  // Reset initialization tracking when forceNewConversation changes
  useEffect(() => {
    if (forceNewConversation) {
      initializedConversationRef.current = null;
    }
  }, [forceNewConversation]);

  // Send one initial request on mount (only for brand-new conversations with no messages)
  useEffect(() => {
    const conversationId = currentConversation?.id;
    const existingCount = currentConversation?.messages?.length || 0;

    // For forced new conversations, always send initial message if no messages exist
    const shouldSendInitialMessage = forceNewConversation
      ? conversationId && existingCount === 0 && practiceMessages.length === 0
      : conversationId &&
        initializedConversationRef.current !== conversationId &&
        existingCount === 0 &&
        practiceMessages.length === 0;

    if (!shouldSendInitialMessage) {
      return;
    }

    // Mark this conversation as initialized
    initializedConversationRef.current = conversationId || null;

    const sendInitialMessage = async () => {
      setIsPracticeThinking(true);

      let initialMessage: string;
      if (segmentToExplain) {
        // Handle segment explanation
        if (segmentToExplain.translation && segmentToExplain.translation.trim()) {
          initialMessage = `Please explain this phrase: "${segmentToExplain.text}"\n\nI see it's translated as: "${segmentToExplain.translation}"\n\nCan you explain the grammar, vocabulary, and cultural context?`;
        } else {
          initialMessage = `Please explain this phrase: "${segmentToExplain.text}"\n\nCan you explain the grammar, vocabulary, and cultural context?`;
        }
        // Add user message to history for segment explanation
        setPracticeMessages([{ role: "user", content: initialMessage }]);
      } else {
        // Handle initial greeting (silent)
        const isLearningEnglish = languageName.toLowerCase().includes("english");
        const instructionLanguage = (isLearningEnglish ? fromLanguageName : "English") || "English";
        initialMessage = `Hello let's start practicing ${languageName} at ${languageLevel} level. The instruction language should be ${instructionLanguage}.`;
      }

      try {
        // The system prompt is now handled by the backend.
        const messages = [{ role: "user", content: initialMessage }];

        const response = await fetch("/api/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages,
            userId: userId || "anonymous",
            agentId: `language-tutor-${userId || "anonymous"}`,
            languageName,
            languageLevel,
            fromLanguageName,
          }),
        });

        if (!response.ok) {
          let errorDetailsMessage = `Failed to fetch response. Status: ${response.status}`;
          try {
            const errorData = (await response.json()) as any;
            if (errorData && errorData.error) {
              errorDetailsMessage = `API Error: ${errorData.error}`;
            }
          } catch (e) {
            errorDetailsMessage = `Failed to fetch response. Status: ${response.status}. Unable to parse error body.`;
          }
          throw new Error(errorDetailsMessage);
        }

        const data = (await response.json()) as any;
        const aiResponse = data.response.content[0].text;

        setPracticeMessages((prev) => [...prev, { role: "assistant", content: aiResponse }]);
      } catch (error: any) {
        logger.error("Error sending initial message", {
          details: error,
          message: error?.message,
          stack: error?.stack,
        });
        const fallbackResponse =
          "Hello! I'm your language tutor. How can I help you practice today?";
        setPracticeMessages((prev) => [...prev, { role: "assistant", content: fallbackResponse }]);
      } finally {
        setIsPracticeThinking(false);
      }
    };

    sendInitialMessage();
  }, [currentConversation?.id, practiceMessages.length, forceNewConversation]); // re-evaluate if the selected conversation changes or messages change

  return (
    <div className="flex h-full min-h-0 flex-col bg-transparent">
      {/* Header */}
      <ChatHeader
        languageName={languageName}
        languageLevel={languageLevel}
        onClose={onClose}
        onToggleSidebar={onToggleSidebar}
        onGenerateWidget={handleGenerateWidget}
        isWidgetLoading={isWidgetLoading}
        onContinueSession={onContinueSession}
      />

      {/* Messages Area */}
      <div
        ref={chatContainerRef}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8"
        id="practice-chat-messages"
      >
        <div className="mx-auto w-full max-w-4xl space-y-6">
          {practiceMessages.length === 0 && (
            <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/[0.04]">
                <BrandMark size={48} className="h-12 w-12 object-contain" alt="Chicky AI Tutor" />
              </div>
              <h2 className="mt-5 text-xl font-bold text-white">What are we writing today?</h2>
              <p className="mt-2 text-sm text-white/45">
                {languageName} / {languageLevel.replace("_", " ")}
              </p>
            </div>
          )}

          {practiceMessages.map((message, index) => (
            <ChatMessage
              key={index}
              role={message.role}
              content={message.content}
              messageIndex={index}
              isCopied={copiedMessageIndex === index}
              onCopy={handleCopyMessage}
            />
          ))}

          {showWidget && (
            <InteractiveWidget
              prompt={widgetPrompt}
              isLoading={isWidgetLoading}
              error={widgetError}
              onClose={closeWidget}
            />
          )}

          {isPracticeThinking && (
            <div className="py-1" role="status" aria-label="Chicky is thinking">
              <p className="mb-2 text-[11px] font-semibold uppercase text-orange-300">Chicky</p>
              <div className="flex space-x-1.5">
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-orange-300/55" />
                <div
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-orange-300/55"
                  style={{ animationDelay: "0.2s" }}
                />
                <div
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-orange-300/55"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="shrink-0">
        <MessageInput
          onSend={handlePracticeMessage}
          isThinking={isPracticeThinking}
          onSummary={handleCreateSummary}
          isSummarizing={isSummarizing}
          onGenerateExercise={handleGenerateWidget}
          onGiveHint={showLearningCanvas ? handleGiveHint : undefined}
          onContinueSession={onContinueSession}
        />
      </div>

      {/* Flow Canvas Widget Overlay */}
      {showLearningCanvas && showFlowCanvas && (
        <FlowCanvasWidget
          messages={flowCanvasMessages}
          isLoading={isFlowCanvasLoading}
          error={flowCanvasError}
          onClose={closeFlowCanvas}
          onSpeakText={handleSpeakText}
          onSelectPath={handleSelectPath}
        />
      )}

      {/* Semantic Branching Canvas - Smart Learning Canvas (hidden on mobile) */}
      {showLearningCanvas && (
        <div className="hidden sm:block">
          <BranchingCanvas
            nodes={branchingCanvas.recentNodes}
            grammarStats={branchingCanvas.grammarStats}
            suggestedQuestions={branchingCanvas.suggestedQuestions}
            weakAreas={branchingCanvas.weakAreas}
            isLoading={branchingCanvas.isLoading}
            isExpanded={branchingCanvas.isExpanded}
            adaptationResult={branchingCanvas.adaptationResult}
            onToggleExpand={branchingCanvas.toggleExpand}
            onNodeSelect={branchingCanvas.handleNodeSelect}
            onAdaptPhrase={(node) => branchingCanvas.handleAdaptPhrase(node)}
            onClearAdaptation={branchingCanvas.clearAdaptationResult}
            onSendQuestion={handleSelectPath}
            selectedNodeId={branchingCanvas.selectedNodeId}
            messageCount={branchingCanvas.messageCount}
          />
        </div>
      )}
    </div>
  );
};

export default PracticeChat;
